import { auth } from '@actions/services/firestore/browser/firebase';
import type { CanvasApiErrorBody, CanvasRequestParams } from '../types';
import getCanvasApiBaseUrl from './getCanvasApiBaseUrl';
import CanvasApiError from './CanvasApiError';

// Single network entry point for every canvas hook: attaches the Firebase
// bearer token and the organizationId the canvas API expects (platform
// convention, same as skills/dashboards) — merged into the JSON body for
// POST/PUT/PATCH requests, or appended as a query param for GET/DELETE and
// FormData bodies — then normalizes responses and errors. FormData bodies
// (multipart item uploads) skip the JSON content type so the browser sets the
// multipart boundary itself.
const canvasRequest = async <TResult>({
    method,
    path,
    organizationId,
    body,
    signal,
}: CanvasRequestParams): Promise<TResult> => {
    const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
    const carriesJsonOrganizationId = !isFormData && method !== 'GET' && method !== 'DELETE';

    const headers: Record<string, string> = {};
    if (!isFormData) {
        headers['Content-Type'] = 'application/json';
    }
    if (auth.currentUser) {
        headers['Authorization'] = `Bearer ${await auth.currentUser.getIdToken()}`;
    }

    const requestUrl = new URL(path, getCanvasApiBaseUrl());
    if (!carriesJsonOrganizationId) {
        requestUrl.searchParams.set('organizationId', organizationId);
    }

    const requestBody = carriesJsonOrganizationId
        ? JSON.stringify({ ...(body as Record<string, unknown> | undefined), organizationId })
        : body === undefined
          ? undefined
          : isFormData
            ? (body as FormData)
            : JSON.stringify(body);

    const response = await fetch(requestUrl.toString(), {
        method,
        headers,
        body: requestBody,
        signal,
    });

    if (response.status === 204) {
        return undefined as TResult;
    }

    const contentType = response.headers.get('content-type') ?? '';
    const isJson = contentType.includes('application/json');
    const payload = isJson ? await response.json() : await response.text();

    if (!response.ok) {
        const errorBody = (
            isJson ? payload : { code: 'UNKNOWN', message: String(payload), details: null }
        ) as CanvasApiErrorBody;
        throw new CanvasApiError({
            code: errorBody.code || 'UNKNOWN',
            message: errorBody.message || 'Request failed',
            status: response.status,
            details: errorBody.details ?? null,
        });
    }

    return payload as TResult;
};

export default canvasRequest;
